/**
 * The document renderer (RFC 0263 slice 4 + RFC 0267 S2): the canvas body for
 * an open document.
 *
 * Hydrates the document's own Automerge doc and branches on mime: markdown as
 * prose you can edit (RFC 0283 S3), HTML in a SANDBOXED iframe (scripts allowed so a micro-app runs;
 * same-origin withheld so it can never touch the app's origin, storage, or
 * repo), else preformatted text.
 *
 * When the open HTML document is an APP VIEW (its id is some app's viewDocId in
 * the project catalog), the iframe also gets the `mod` bridge (RFC 0267 §5b):
 * a shim is injected so the view's `mod.type.*` calls become postMessage RPC
 * the host answers with real mod-core mutations, scoped to that app's data
 * type. Writes persist and sync because instances are Automerge docs; the host
 * notifies the frame on any change so it re-renders live.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo'
import { useRepo } from '@automerge/automerge-repo-react-hooks'
import {
  linkedCommentsIds,
  type ProjectDoc,
  projectDocuments,
  projectDocumentsOfType,
  readApp,
  readDocumentBytes,
  readDocumentContent,
  readDocumentData,
} from '@mod/core/model'
// The editor the window ships, as one ES module (RFC 0314 S7).
import SHIPPED_EDITOR from '@mod/editor/dist/editor.js?raw'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { App } from '../../sdk/host/App'
import { useContribution } from '../../sdk/host/useContribution'
import { handleBridgeCall, injectBridge } from '../../sdk/sandboxed/appBridge'
import { forwardedKey } from '../../sdk/sandboxed/frameShortcuts'
import { loadGuestAssets } from '../../sdk/sandboxed/guestAssets'
import {
  createSandboxSync,
  type SandboxSync,
} from '../../sdk/sandboxed/sandboxSync'
import {
  createSyncGate,
  denyInfrastructure,
  seedAllow,
} from '../../sdk/sandboxed/syncGate'
import { sandboxWindowCall } from '../../sdk/sandboxed/windowBridge'
import { useWindow } from '../../sdk/WindowProvider'
import {
  type AppViewController,
  createAppViewController,
} from '../../services/apps/appControl'
import {
  useInstalledViewId,
  useLiveVersion,
} from '../../services/apps/liveVersion'
import {
  useBranchCopyOf,
  useBranchDocument,
} from '../../services/branches/branchSelection'
import { followDocument } from '../../services/comments/followDocument'
import { labelFrom, useSelection } from '../../services/sessions/selection'
import {
  routeComment,
  useSpaceSession,
} from '../../services/sessions/spaceSession'
import { repaintWhenShown } from '../../services/view/repaintWhenShown'
import { useDocument } from '../../services/world/repo/useDocument'
import { useInstalledApps } from '../../services/world/useInstalledApps'
import { useWorldHandlers } from '../../services/world/useWorldHandlers'
import { ContributionWaiting } from '../../shell/slots/ContributionWaiting'
import { handlerFor } from '../../shell/slots/renderers'
import type { Subject } from '../../shell/window/subject'
import type { CanvasViewMode } from '../../shell/window/ViewModeMenu'
import { appCommentsKey, appViewInstancesKey } from './appViewInstances'
import { DocumentComments } from './DocumentComments'
import type { LinkOpenTarget } from './DocumentLink'
import { useEditPause } from './editPause'
import { FieldsView } from './FieldsView'
import { FileView } from './FileView'
import { MarkdownReader } from './MarkdownReader'
import { NewDocumentGate } from './NewDocumentGate'
import { NewInstanceGate } from './NewInstanceGate'

export function DocumentView({
  file,
  projectId,
  onOpenSession,
  onSubject,
  onOpenDocument,
  onOpenUrl,
  onAppControl,
  surface = 'canvas',
  surfaceContext,
  viewMode = 'app',
}: {
  file: Subject
  projectId: string
  /** Host navigation for an app view's `mod.sessions.open` (RFC 0267): open a
   *  session in the shell's agent panel. Absent for a plain document. */
  onOpenSession?: (id: string) => void
  /** RFC 0273: the view told us what it is showing. */
  onSubject?: (id: string | null, include?: string[]) => void
  /** RFC 0273: open a document on the canvas, like onOpenSession for sessions. */
  onOpenDocument?: (id: string, target?: LinkOpenTarget) => void
  /** Modified web links can ask the shell for a split or a new space. */
  onOpenUrl?: (url: string, target?: LinkOpenTarget) => void
  /** RFC 0328: the mounted frame's command door, owned by its pane. */
  onAppControl?: (controller: AppViewController | null) => void
  /** RFC 0273: which column this view is mounted in. */
  surface?: 'canvas' | 'panel' | 'sidebar' | 'sessionHeader'
  /** Plain slot identity, updated over the bridge without reloading the view. */
  surfaceContext?: { projectId?: string; sessionId?: string }
  /** How to look at it: the rendered app, or the bytes underneath. */
  viewMode?: CanvasViewMode
}) {
  // RFC 0271: an INSTANCE opens as itself but is DRAWN by its app's view, so
  // the content to load is the view while the identity stays the instance.
  //
  // RFC 0274: and the view is looked up LIVE from the app rather than trusted
  // from the open file, because a republish can point the app at a fresh
  // document. A stale pointer here renders the previous build, silently, until
  // something else forces a remount.
  const [wsForView] = useDocument(projectId as AnyDocumentId)
  /**
   * Both catalogs, and the person's choice (RFC 0284 S6m, RFC 0295).
   *
   * Which app owns a record is asked of both as well: an app installed in
   * the World draws its records in every project (RFC 0338 3g).
   */
  const installedForView = useInstalledApps(wsForView)
  const worldHandlers = useWorldHandlers()
  const liveViewDocId = useMemo(() => {
    if (!file.appId) return undefined
    return installedForView.find((a) => a.id === file.appId)?.viewDocId
  }, [installedForView, file.appId])
  // RFC 0313 3j: on a Mod branch, a plain document opens as the branch's copy,
  // made when the pane opens it, so what you type goes to the branch and main
  // never sees it until the branch merges. An app's view is not branched here.
  const onBranch = useBranchDocument(projectId, file.id, !file.appId)
  // The view as main runs it: the one the installed version named, which a
  // repointed but uninstalled app does not change (0313 adversarial, 6).
  const installedViewId = useInstalledViewId(
    projectId,
    file.appId,
    liveViewDocId ?? file.viewDocId,
  )
  const mainViewDocId = installedViewId ?? liveViewDocId ?? file.viewDocId
  // An app's view runs the branch's copy once the branch has edited it.
  const viewOnBranch = useBranchCopyOf(
    projectId,
    file.appId ? mainViewDocId : undefined,
  )
  const contentDocId = viewOnBranch ?? mainViewDocId ?? onBranch.id ?? file.id
  // Who owns this view, asked by main's id: a branch copy is in no catalog.
  const catalogDocId = mainViewDocId ?? file.id
  // An app opened BY NAME resolves to the app's own id, so "is this an
  // instance" is now: the open file is not the view it is drawn with.
  const instanceId = file.appId && file.id !== file.appId ? file.id : undefined

  const [editedDoc] = useDocument(contentDocId as AnyDocumentId)
  // RFC 0313 3m: an installed app draws its view as its live version saved it,
  // so editing the view does not change what anyone runs until it is installed.
  const viewAppId = useMemo(
    () =>
      file.appId ??
      installedForView.find((a) => a.viewDocId === catalogDocId)?.id,
    [installedForView, catalogDocId, file.appId],
  )
  // On a branch the app runs as the branch edited it, not its live version.
  const doc = useLiveVersion(
    projectId,
    viewOnBranch ? undefined : viewAppId,
    contentDocId,
    editedDoc,
  )
  const [projectDoc] = useDocument(projectId as AnyDocumentId)
  // RFC 0301 S6: a pause in typing reaches the sessions paired to this
  // document. Plain documents only: an app's view and a record's fields are not
  // prose a line diff can show.
  const editRepo = useRepo() as unknown as Repo
  useEditPause(editRepo, projectId, file.appId ? undefined : file.id)
  // The RECORD, when one is open. `doc` is deliberately the view (that is what
  // draws the page), so every mode that reports on the document itself must
  // read this instead — otherwise a review's own fields are reported as the
  // view's, which have never existed, and the record reads as empty.
  const [instanceDoc] = useDocument(instanceId as AnyDocumentId | undefined)
  const subjectDoc = instanceId ? instanceDoc : doc

  // Is this document an app's view? If so, the app's data type scopes the
  // bridge. Plain documents resolve to undefined and render with no bridge.
  // By the app first: an installed app may run an older view than its record
  // names now (finding 6), and its grants must follow the app, not the view.
  const appSummary = useMemo(
    () =>
      (viewAppId
        ? installedForView.find((a) => a.id === viewAppId)
        : undefined) ??
      installedForView.find((a) => a.viewDocId === catalogDocId),
    [installedForView, catalogDocId, viewAppId],
  )
  const dataType = appSummary?.dataType
  // The app's capability grants (capabilities.reads) live in its own document,
  // not the thin catalog entry, so hydrate it. Default-DENY: until it loads (or
  // for a legacy app with no app doc) the view gets no cross-interface reads.
  const [appDoc] = useDocument(appSummary?.id as AnyDocumentId | undefined)
  const grants = useMemo(
    () => readApp(appDoc)?.capabilities?.reads ?? [],
    [appDoc],
  )
  // Write grants gate the write interfaces the bridge exposes (comments today).
  // Same default-DENY: empty until the app doc hydrates.
  const writeGrants = useMemo(
    () => readApp(appDoc)?.capabilities?.writes ?? [],
    [appDoc],
  )

  /**
   * A token that changes when the view's BYTES change, for the frame's key.
   *
   * An iframe takes its content from `srcDoc` when it LOADS. Updating the
   * attribute on a frame that has already loaded does not re-navigate it, so a
   * frame mounted while its document was still hydrating loads EMPTY and then
   * stays empty forever, while the DOM shows the correct srcdoc. From outside
   * the frame that is indistinguishable from an app that renders nothing: right
   * document, right size, right bridge, no paint, no error.
   *
   * Keying on the content forces a real remount, which is the only thing that
   * makes a frame re-read srcDoc. It also makes a republish (RFC 0274) take
   * effect without a page reload, which the previous key could not do: it held
   * only the document id, and a rebuild keeps the id.
   *
   * ABOVE the hydration gate below, because hooks cannot run after a
   * conditional return. A hash rather than the content itself so React compares
   * a short string instead of 119KB on every render.
   */
  /**
   * WHO EDITS THIS DOCUMENT (RFC 0275 §4, RFC 0284 S6m).
   *
   * Read off the thin catalog, not by hydrating every app: opening any document
   * asks this question, and paying an app-doc load per installed app to answer
   * it would undo the property the catalog exists for.
   *
   * THROUGH `handlerFor`, and both halves of that were bugs. It was a `find`
   * over `projectApps`, so a World-installed editor (RFC 0295) was never found
   * for a document at all; and it was a second copy of the rule a session leaf
   * uses, free to drift. One function answers both now, and it knows about a
   * person's choice when more than one package claims a type.
   */
  const editorApp = useMemo(
    () => handlerFor(installedForView, file.mime, worldHandlers),
    [installedForView, file.mime, worldHandlers],
  )
  // The same three steps a slot and a thread renderer take, through the same
  // hook. `absent` is the ordinary case: no package claims this mime and the
  // built-in draws. `loading` used to fall through to the built-in TOO, so a
  // person saw the shipped editor and then, when the code arrived, a different
  // one; it says so now instead.
  const editor = useContribution(
    editorApp?.realm === 'host' ? editorApp.viewDocId : undefined,
  )
  const editorView = editor.code

  /**
   * What a sandboxed app may NEVER sync, computed where the catalog is in
   * scope (RFC 0275). The project document leads the list because it IS the
   * catalog: handing it over hands over every id at once, and under this
   * threat model a learned id is eventually reachable.
   */
  const sandboxDenyIds = useMemo(() => {
    const ws = projectDoc as ProjectDoc | undefined
    return {
      projectId,
      sessionIds: Object.keys(ws?.sessionMeta ?? {}),
      appDocIds: installedForView.flatMap((a) =>
        [a.id, a.viewDocId, a.skillDocId].filter(Boolean),
      ),
    }
  }, [projectDoc, projectId, installedForView])

  /** What it MAY sync: the record it is drawing, and nothing else until it
   *  declares a subject. Discovery widens this from the record's own content. */
  const sandboxGrantIds = useMemo(
    () => (instanceId ? [instanceId] : []),
    [instanceId],
  )

  const viewToken = useMemo(() => {
    const c = doc ? readDocumentContent(doc) : ''
    let h = 5381
    for (let i = 0; i < c.length; i++) {
      h = ((h << 5) + h + c.charCodeAt(i)) | 0
    }
    return `${c.length}.${(h >>> 0).toString(36)}`
  }, [doc])

  // Wait for BOTH the document and the project catalog before rendering.
  // Deciding app-vs-plain needs the catalog: if we render an app view as a
  // plain (bridge-less) iframe while the project doc is still loading, the
  // view breaks (it requires `mod`) and shows a white flash before flipping to
  // the bridged AppView. Gate on the catalog so an app view never renders
  // without its bridge.
  // RFC 0313 3j: while the branch's copy is being made, show nothing editable,
  // or a keystroke in that instant would land on main.
  if (onBranch.branch && !onBranch.id) {
    return (
      <div className="flex flex-1 items-center justify-center text-muted-foreground text-xs">
        Opening on {onBranch.branch}…
      </div>
    )
  }
  if (doc === undefined || projectDoc === undefined) {
    return (
      <div className="flex flex-1 items-center justify-center text-muted-foreground/60 text-sm">
        Loading {file.title}…
      </div>
    )
  }

  const content = readDocumentContent(doc)
  // PREVIEW: the document to be read, not worked on.
  //
  // Not a branch of its own, because a preview of a document is not a DIFFERENT
  // rendering of it — it is the same rendering with the working affordances
  // withdrawn. So it rides along the ordinary paths below as a flag: the
  // markdown branch drops its Edit button and widens, the plain-text branch gets
  // a measure instead of the full width, and an app view is left exactly as it
  // is (a rendered app is already only its finished self, which is the whole
  // point of the mode).
  const reading = viewMode === 'preview'

  // DATA: the structured fields ONLY, with document references followed rather
  // than printed as opaque ids.
  //
  // Deliberately NOT the body. Data and Source rendered the same thing for any
  // content-only document (an app view has no fields, so "fields plus body" is
  // just the body), which makes two modes that look like a mistake. Each mode
  // now means exactly one thing: Data is what the record IS, Source is what it
  // SAYS. A nested reference still shows its body, because there is no way to
  // switch modes inside an expansion.
  if (viewMode === 'data') {
    // An APP's data is the records it holds, not the (empty) fields of its view
    // document. The rendered app shows them as a list; this shows the same
    // records structurally, each expandable into its own fields and body.
    // Opening one of those records directly falls through to the second case.
    const records =
      dataType && !instanceId
        ? projectDocumentsOfType(projectDoc, dataType)
        : undefined
    return (
      <div className="min-h-0 flex-1 overflow-auto bg-background">
        <FieldsView
          data={readDocumentData(subjectDoc)}
          documents={projectDocuments(projectDoc)}
          records={records}
        />
      </div>
    )
  }

  // SOURCE: the document's own text, always. Never the bridged iframe, so a
  // view that throws on boot is still readable — which is exactly when you need
  // to read it.
  //
  // It used to render an instance's FIELDS as JSON, which was Data's job and
  // left an instance's actual body unreachable from either mode.
  if (viewMode === 'source') {
    return (
      <div className="min-h-0 flex-1 overflow-auto bg-background p-3">
        <pre className="whitespace-pre-wrap break-words font-mono text-[12px] text-muted-foreground leading-relaxed">
          {readDocumentContent(subjectDoc) ||
            '(no body — this document is fields only, see Data)'}
        </pre>
      </div>
    )
  }

  // RFC 0271 S4: selecting a DOCUMENT app makes one. Its records are already
  // listed in the sidebar and in ⌘T, so a third list here would be duplication
  // and only its New button would have earned a place. Selecting the app IS
  // New, so it mints (or reuses an untouched record) and opens it.
  if (dataType && !instanceId && appSummary?.presentation === 'document') {
    return (
      <NewInstanceGate
        appName={appSummary.name}
        dataType={dataType}
        projectId={projectId}
        records={projectDocumentsOfType(projectDoc, dataType)}
        onOpen={(id) => onOpenDocument?.(id)}
      />
    )
  }

  // OPENING AN EDITOR MAKES A DOCUMENT (RFC 0275 §4).
  //
  // Apps come in three kinds and the sidebar offers one row for all three: one
  // you open (a poll), one that mints a record when you open it (a review), and
  // one that edits OTHER documents (the editor). The third had no rule, so it
  // fell through to "render the app's view document" — which for a host-realm
  // editor is a JavaScript module, and showed a screenful of minified bundle.
  //
  // I replaced that with a card explaining there was nothing to open. Also
  // wrong, and wrong in a more annoying way: clicking TextEdit gives you a
  // blank document, not a note about what TextEdit is for. Selecting the editor
  // IS New — the same rule RFC 0271 already applies to document apps, with the
  // one difference that this mints an ordinary document of the type it handles
  // rather than an instance of a type it owns.
  if (appSummary?.handles?.length && !appSummary.dataType && !instanceId) {
    const editedMime = appSummary.handles[0] as string
    return (
      <NewDocumentGate
        appName={appSummary.name}
        mime={editedMime}
        projectId={projectId}
        documents={projectDocuments(projectDoc).filter(
          (d) => d.mime === editedMime,
        )}
        onOpen={(id) => onOpenDocument?.(id)}
      />
    )
  }

  if (file.mime === 'text/html') {
    if (dataType) {
      // Never mount the frame with nothing in it. `doc` is undefined until the
      // view document hydrates, and a frame that loads empty cannot be filled
      // in afterwards (see viewToken) — it just renders blank forever.
      if (!content) {
        return (
          <div className="flex min-h-0 flex-1 items-center justify-center p-6">
            <p className="text-muted-foreground/60 text-sm">
              Loading {file.title}…
            </p>
          </div>
        )
      }
      return (
        <AppView
          // Keyed on all three: switching between two instances of the same app
          // must remount, or the view keeps the previous record's state while
          // the bridge reports the new id — and a REBUILT view must remount too,
          // which the id alone cannot express because a rebuild keeps the id.
          key={`${contentDocId}:${instanceId ?? ''}:${viewToken}`}
          title={file.title}
          instanceId={instanceId}
          html={content}
          projectId={projectId}
          dataType={dataType}
          grants={grants}
          writeGrants={writeGrants}
          onOpenSession={onOpenSession}
          onSubject={onSubject}
          onOpenDocument={onOpenDocument}
          onAppControl={onAppControl}
          surface={surface}
          surfaceContext={surfaceContext}
          sync={appSummary?.sync}
          denyIds={sandboxDenyIds}
          grantIds={sandboxGrantIds}
        />
      )
    }
  }
  // A plain page an installed app claims (`handles: ['text/html']`, the HTML
  // app, with commenting) goes to that app in the type dispatch below. This
  // block used to take every page first, so no app could ever claim one.
  if (file.mime === 'text/html' && editorApp?.realm !== 'host') {
    return (
      <iframe
        // Same reason as the app frame: an iframe reads srcDoc at load, so one
        // mounted before its document hydrated stays blank however many times
        // the attribute is updated afterwards. Remount when the bytes change.
        key={viewToken}
        ref={repaintWhenShown}
        title={file.title}
        sandbox="allow-scripts"
        // Same as the app frame below: fullscreen is delegated by Permissions
        // Policy, and a plain HTML document that presents needs it too.
        allow="fullscreen"
        srcDoc={content}
        // An iframe composites in its own layer, so the parent's
        // overflow-hidden does not reliably clip it: left square, its corner
        // shows just inside the parent's arc and reads as a doubled border.
        // It therefore rounds ITSELF — but via `inherit`, because Panel decides
        // the radius conditionally (`framed`), and a hardcoded value would draw
        // corners on a panel that deliberately has none.
        className="h-full w-full flex-1 rounded-[inherit] border-0 bg-white"
      />
    )
  }

  // TYPE DISPATCH (RFC 0275 §4). Who edits this document is a question the
  // catalog answers, not a branch here: the app declaring the mime wins, the
  // same way an instance already resolves to the app declaring its dataType.
  //
  // Registration enforces one app per type, so `find` returning the first match
  // is safe by construction rather than by luck.
  // COMMENTING FOLLOWS THE EDITOR, not one branch of the dispatch (RFC 0289
  // S5). It used to live inside the markdown editor, which was the FALLBACK branch,
  // so the day an app declared `handles: ['text/markdown']` the branch above
  // would win and selecting a passage would silently stop offering to comment
  // on it. Wrapping here means whichever editor the catalog picks, a person can
  // still say something about a sentence in it.
  //
  // A host-realm app gets a NON-SCROLLING wrapper: it lays itself out and owns
  // its own scrolling, and imposing `overflow-y-auto` on it would nest two
  // scrollbars.
  if (editorApp?.realm === 'host' && editor.state === 'loading') {
    return (
      <ContributionWaiting
        name={editorApp.name}
        what="the editor's"
        recovery="Settings (⌘,) then Open with brings the shipped editor back."
      />
    )
  }
  if (editorApp?.realm === 'host' && editor.state === 'ready') {
    return (
      <EditorPane
        // Remount when the editor is rebuilt or the document changes, for the
        // same reason the app frame does: a mounted editor holds a handle and
        // will not notice either on its own. The key is on THIS wrapper rather
        // than on the app, so the commenting claim below is thrown away with
        // the editor that made it — a different app on the next document must
        // not inherit the last one's claim.
        key={`${editorApp.id}:${editorView.length}:${file.id}`}
        code={editorView}
        documentId={contentDocId}
        projectId={projectId}
        title={editorApp.name}
        onOpenDocument={onOpenDocument}
        onAppControl={onAppControl}
      />
    )
  }

  // A FILE (a PDF, an image) that no installed app draws: after the type
  // dispatch, so the PDF reader app, once installed, is what opens a PDF.
  const bytes = readDocumentBytes(doc)
  if (bytes) {
    return (
      <DocumentComments projectId={projectId} documentId={file.id}>
        <FileView
          bytes={bytes}
          mime={file.mime}
          title={file.title}
          sourceKey={`${contentDocId}:${bytes.byteLength}`}
        />
      </DocumentComments>
    )
  }

  if (file.mime === 'text/markdown') {
    // READING MODE is the window's own render (RFC 0297 §5a): the same prose
    // set to be read, with document links and embeds resolved.
    if (reading) {
      return (
        <DocumentComments projectId={projectId} documentId={file.id} disabled>
          <MarkdownReader
            documentId={contentDocId}
            projectId={projectId}
            content={content}
            onOpenDocument={onOpenDocument}
            onOpenUrl={onOpenUrl}
          />
        </DocumentComments>
      )
    }
    // EDITING with no app installed (RFC 0314 S7): the editor the window ships
    // is `@mod/editor`, the same CodeMirror module the World's text-editor app
    // publishes, bundled at build time and mounted through the same host-realm
    // path an installed editor takes. So a fresh World and an installed one
    // open markdown in the same editor, and an app that declares
    // `handles: ['text/markdown']` still wins (the branch above).
    return (
      <EditorPane
        key={`shipped-editor:${file.id}`}
        code={SHIPPED_EDITOR}
        documentId={contentDocId}
        projectId={projectId}
        title="Editor"
        onOpenDocument={onOpenDocument}
        onAppControl={onAppControl}
      />
    )
  }

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <pre
        className={
          reading
            ? 'mx-auto w-full max-w-3xl whitespace-pre-wrap px-8 py-12 font-mono text-[13px] text-foreground leading-relaxed'
            : 'whitespace-pre-wrap px-6 py-8 font-mono text-foreground text-sm'
        }
      >
        {content}
      </pre>
    </div>
  )
}

/**
 * A host-realm editor, with commenting from whichever side has it.
 *
 * COMMENTING FOLLOWS THE EDITOR (RFC 0289 S5), and the host's popover is the
 * fallback for the editors that have none — which is most of them. An editor
 * that has its own says so during mount, and the host stands its popover down
 * rather than laying a second Comment button over the first.
 *
 * The claim is STATE rather than a lookup because it arrives asynchronously:
 * the app is fetched, turned into a module and mounted, and until it has, the
 * host cannot know. Starting with the popover on is the safe default — an
 * editor that never claims is one you can still comment in.
 */
function EditorPane({
  code,
  documentId,
  projectId,
  title,
  onOpenDocument,
  onAppControl,
}: {
  code: string
  documentId: string
  projectId: string
  title: string
  /** RFC 0297 §5a: the editor that draws markdown is the one people read it
   *  in, so the document links have to work there and not only in read mode. */
  onOpenDocument?: (id: string, target?: LinkOpenTarget) => void
  /** RFC 0328 3d: the editor's view commands, for `control_app`. */
  onAppControl?: (controller: AppViewController | null) => void
}) {
  const [editorComments, setEditorComments] = useState(false)
  return (
    <DocumentComments
      projectId={projectId}
      documentId={documentId}
      className="relative flex min-h-0 flex-1 flex-col"
      disabled={editorComments}
    >
      <App
        code={code}
        documentId={documentId}
        projectId={projectId}
        title={title}
        onClaimComments={() => setEditorComments(true)}
        onOpenDocument={onOpenDocument}
        onAppControl={onAppControl}
      />
    </DocumentComments>
  )
}

/** An app view: the sandboxed iframe plus the host side of the `mod` bridge. */
function AppView({
  title,
  html,
  projectId,
  dataType,
  grants,
  writeGrants,
  onOpenSession,
  onSubject,
  onOpenDocument,
  onAppControl,
  surface = 'canvas',
  surfaceContext,
  instanceId,
  sync,
  denyIds,
  grantIds,
}: {
  title: string
  html: string
  projectId: string
  dataType: string
  grants: string[]
  writeGrants: string[]
  /** RFC 0271: the instance this view is drawing, exposed to it as
   *  `mod.instanceId`. Absent when the app was opened by name. */
  instanceId?: string
  onOpenSession?: (id: string) => void
  /** RFC 0273: this view declared what it is showing. */
  onSubject?: (id: string | null, include?: string[]) => void
  /** RFC 0273: open a document on the canvas, like onOpenSession for sessions. */
  onOpenDocument?: (id: string) => void
  /** Publish this mounted frame's local command channel to its pane. */
  onAppControl?: (controller: AppViewController | null) => void
  /** RFC 0273: which column this view is mounted in. */
  surface?: 'canvas' | 'panel' | 'sidebar' | 'sessionHeader'
  surfaceContext?: { projectId?: string; sessionId?: string }
  /** RFC 0275: this app asked for real handles, and accepts the ~4MB frame
   *  cost. Absent means no repo is built and no port is transferred. */
  sync?: 'documents'
  /** Never syncable to a sandboxed app, whatever it asks for. */
  denyIds?: {
    projectId: string
    sessionIds?: string[]
    worldId?: string | null
    appDocIds?: string[]
  }
  /** Seeds for what it may sync. Discovery widens from these. */
  grantIds?: string[]
}) {
  const repo = useRepo() as unknown as Repo
  const windowCapability = useWindow()
  const windowRef = useRef(windowCapability)
  windowRef.current = windowCapability
  const contextRef = useRef(surfaceContext ?? {})
  contextRef.current = surfaceContext ?? {}
  const currentSurfaceContext = useCallback(() => {
    if (surface !== 'sidebar') return contextRef.current
    const view = windowRef.current?.read()
    return {
      ...contextRef.current,
      spaceId: view?.space ?? null,
      focusedLeafId: view?.focused ?? null,
    }
  }, [surface])
  const iframeRef = useRef<HTMLIFrameElement>(null)
  // Stable, so React attaches the repaint watcher once per frame element.
  const frameRef = useCallback((el: HTMLIFrameElement | null) => {
    iframeRef.current = el
    return repaintWhenShown(el)
  }, [])
  // The frame's sync channel, when it opted in. Torn down and rebuilt per
  // handshake: a MessagePort transfers once, and the frame reloads on every
  // srcDoc change.
  const syncRef = useRef<SandboxSync | null>(null)
  const gateIdsRef = useRef(denyIds ?? { projectId })
  gateIdsRef.current = denyIds ?? { projectId }
  const grantedIdsRef = useRef<string[]>(grantIds ?? [])
  grantedIdsRef.current = grantIds ?? []
  // Keep the latest navigator in a ref so the once-bound handler always calls the
  // current one (same pattern as grants).
  const onOpenSessionRef = useRef(onOpenSession)
  onOpenSessionRef.current = onOpenSession
  // Ref, not a dep: the bridge listener is installed once per view and must not
  // be torn down and reinstalled because a parent re-rendered.
  const onSubjectRef = useRef(onSubject)
  onSubjectRef.current = onSubject
  const onOpenDocumentRef = useRef(onOpenDocument)
  onOpenDocumentRef.current = onOpenDocument
  const onAppControlRef = useRef(onAppControl)
  onAppControlRef.current = onAppControl

  // RFC 0297 §4e. WHAT the view selected is the view's business; WHICH DOCUMENT
  // it is inside is the host's, and the host already knows because §4b's `open`
  // rule put that document in the session's hands. So the selection needs no
  // document id from the app, and the app is never asked to name one it could
  // get wrong.
  const { select } = useSelection()
  const selectRef = useRef(select)
  selectRef.current = select
  // RFC 0301 §3d: where a comment the view posts goes. Same ref treatment.
  const space = useSpaceSession()
  const spaceRef = useRef(space)
  spaceRef.current = space
  // The record this view is drawing. The instance it was opened on until the
  // view says otherwise with `setSubject`; a canvas app that navigates between
  // records would otherwise attach every later selection to the first one.
  const subjectRef = useRef<string | null>(instanceId ?? null)
  useEffect(() => {
    subjectRef.current = instanceId ?? null
  }, [instanceId])
  // The shim runs before the view's script, so the bridge is ready on load.
  const srcDoc = useMemo(
    () => injectBridge(html, dataType, surface, instanceId, sync),
    [html, dataType, surface, instanceId, sync],
  )
  // Keep the latest grants in a ref so the message handler (bound once per
  // effect) always enforces the current capability set without re-binding.
  const grantsRef = useRef(grants)
  grantsRef.current = grants
  const writeGrantsRef = useRef(writeGrants)
  writeGrantsRef.current = writeGrants

  const contextProjectId = surfaceContext?.projectId
  const contextSessionId = surfaceContext?.sessionId
  useEffect(() => {
    const view = surface === 'sidebar' ? windowRef.current?.read() : null
    iframeRef.current?.contentWindow?.postMessage(
      {
        __modBridge: 'surface-context',
        context: {
          ...(contextProjectId ? { projectId: contextProjectId } : {}),
          ...(contextSessionId ? { sessionId: contextSessionId } : {}),
          ...(view ? { spaceId: view.space, focusedLeafId: view.focused } : {}),
        },
      },
      '*',
    )
  }, [contextProjectId, contextSessionId, surface])

  useEffect(
    () =>
      windowCapability?.onChange(() => {
        const frame = iframeRef.current?.contentWindow
        frame?.postMessage({ __modBridge: 'window-change' }, '*')
        if (surface === 'sidebar')
          frame?.postMessage(
            {
              __modBridge: 'surface-context',
              context: currentSurfaceContext(),
            },
            '*',
          )
      }),
    [windowCapability, surface, currentSurfaceContext],
  )

  // The app doc (which carries capabilities.reads) hydrates separately from the
  // view, so grants can start empty and populate a beat later. That is
  // fail-closed (an ungranted read is only ever wrongly denied, never wrongly
  // served), but the denial would otherwise stick for the session: the change
  // watcher below notifies only on DATA changes, not grant changes. Re-fire the
  // view's change subscribers when grants change so a view that read on load
  // re-reads with its now-known capabilities. Harmless for plain/legacy apps
  // (an extra change ping) and, unlike gating render on the app doc, it can't
  // hang a legacy app whose catalog id has no backing doc.
  // `grants`/`writeGrants` are memoized on the app doc, so this fires only when
  // capabilities change (empty -> populated once the app doc hydrates). Skip the
  // empty case: nothing new to re-read when a view already loaded with no grants.
  useEffect(() => {
    if (grants.length === 0 && writeGrants.length === 0) return
    iframeRef.current?.contentWindow?.postMessage(
      { __modBridge: 'change' },
      '*',
    )
  }, [grants, writeGrants])

  // `sync` and `title` are read ONCE, when the bridge hands the frame its ready
  // message. Listing them would tear the iframe down and rebuild it whenever an
  // app's title changed, which reloads somebody's open app to no purpose. The
  // finding pre-dates this file's commenting change; suppressed rather than
  // fixed because the rule now blocks any commit touching this file.
  // biome-ignore lint/correctness/useExhaustiveDependencies: read once at bridge handshake; listing them remounts the iframe
  useEffect(() => {
    const iframe = iframeRef.current
    if (!iframe) return
    let cancelled = false
    const control = createAppViewController((message) =>
      iframe.contentWindow?.postMessage(message, '*'),
    )
    onAppControlRef.current?.(control.control)

    // Answer the frame's mod.type.* calls with real mutations.
    const onMessage = async (e: MessageEvent) => {
      if (e.source !== iframe.contentWindow) return // only this frame
      const d = e.data as {
        __modBridge?: string
        id?: number | string
        method?: string
        args?: unknown[]
      }
      if (control.receive(d)) return
      // Handshake: the frame retries 'hello' until we answer 'ready'; only then
      // does it flush its queued calls, so nothing is lost to attach timing.
      if (d?.__modBridge === 'hello') {
        // RFC 0275: an app that opted into document sync gets ONE MessagePort,
        // transferred with the ready answer. It is the app's only route to any
        // document: no host repo, no storage, no subduction connection, no
        // ticket. Everything it may reach is decided by the gate behind that
        // port, before the app can ask.
        //
        // Built HERE rather than at mount because a port is transferable once,
        // and the frame reloads on every srcDoc change (a republish, a
        // remount). Creating it per handshake means the app that just booted is
        // the one holding a live port.
        const transfer: Transferable[] = []
        let runtime: string | undefined
        let wasm: Uint8Array | undefined
        if (sync === 'documents') {
          syncRef.current?.stop()
          const gate = seedAllow(
            denyInfrastructure(createSyncGate(), gateIdsRef.current),
            grantedIdsRef.current,
          )
          const sandbox = createSandboxSync(repo, gate, title)
          syncRef.current = sandbox
          transfer.push(sandbox.guestPort)
          // Fetched once per host and cached: the frame cannot fetch anything
          // itself, so both of these travel as data. `guestAssets` resolves
          // before the ready answer is sent, because a frame told sync is on
          // and then handed nothing would boot into a rejected promise.
          const assets = await loadGuestAssets()
          runtime = assets.runtime
          wasm = assets.wasm
        }
        iframe.contentWindow?.postMessage(
          {
            __modBridge: 'ready',
            sync: sync ?? null,
            runtime,
            wasm,
            context: currentSurfaceContext(),
          },
          '*',
          transfer,
        )
        return
      }
      if (d?.__modBridge === 'key') {
        // A window shortcut pressed inside the frame, replayed on the window's
        // document so its keymap sees it. forwardedKey re-checks the allowlist.
        const init = forwardedKey(d)
        if (init) document.dispatchEvent(new KeyboardEvent('keydown', init))
        return
      }
      if (d?.__modBridge === 'error') {
        console.error('[app view]', (d as { message?: string }).message)
        return
      }
      if (d?.__modBridge !== 'call' || typeof d.id !== 'number') return
      const call = String(d.method)
      void (
        call.startsWith('window.')
          ? Promise.resolve().then(() =>
              sandboxWindowCall(windowRef.current, surface, call, d.args ?? []),
            )
          : handleBridgeCall(
              repo,
              projectId,
              dataType,
              call,
              d.args ?? [],
              grantsRef.current,
              writeGrantsRef.current,
              // The person at this window, asked for on their first comment.
              undefined,
              {
                openSession: (id: string) => onOpenSessionRef.current?.(id),
                setSubject: (id: string | null, include?: string[]) => {
                  subjectRef.current = id
                  onSubjectRef.current?.(id, include)
                },
                openDocument: (id: string) => onOpenDocumentRef.current?.(id),
                routeComment: (comment) =>
                  routeComment(repo, projectId, spaceRef.current, comment),
                space: () => ({
                  sessionId: spaceRef.current.sessionId,
                  canStart: Boolean(spaceRef.current.start),
                }),
                setSelection: (sel) => {
                  // A selection in a view that has not said what it is showing has no
                  // document to belong to. Dropped rather than guessed: attaching it
                  // to the wrong record would tell the agent "this" meant something
                  // it did not.
                  const documentId = subjectRef.current
                  if (!documentId) return
                  // Null is a no-op, not a clear (§4e). Clicking into the composer
                  // collapses a canvas selection too, and honouring that here would
                  // drop the passage at the moment somebody went to type about it.
                  if (!sel) return
                  const label = labelFrom(sel.label)
                  if (!label) return
                  selectRef.current({
                    documentId,
                    label,
                    ...(sel.ref ? { ref: sel.ref } : {}),
                  })
                },
              },
            )
      )
        .then((result) =>
          iframe.contentWindow?.postMessage(
            { __modBridge: 'result', id: d.id, result },
            '*',
          ),
        )
        .catch((err: unknown) =>
          iframe.contentWindow?.postMessage(
            {
              __modBridge: 'result',
              id: d.id,
              error: err instanceof Error ? err.message : String(err),
            },
            '*',
          ),
        )
    }
    window.addEventListener('message', onMessage)

    // Notify the frame on this app's data change, so it re-renders live. A
    // project document also changes for sessions, tasks, comments and every
    // other app. Treating ALL of those as a change to this app made a deck
    // repeatedly reload its complete slide list while an agent was writing.
    // Coalesce a burst too: the iframe is a separate realm, so each change
    // crosses a structured-clone boundary and may make the app read its list.
    let notifyTimer: ReturnType<typeof setTimeout> | null = null
    const notify = () => {
      if (notifyTimer) return
      notifyTimer = setTimeout(() => {
        notifyTimer = null
        iframe.contentWindow?.postMessage({ __modBridge: 'change' }, '*')
      }, 75)
    }
    // Instance-doc unsubscribes ONLY (keyed by instance id). The project-doc
    // unsubscribe is kept separate (projectOff) so the resubscribe drop-loop
    // never removes the project watcher itself.
    const instanceOff = new Map<string, () => void>()
    let projectOff: (() => void) | null = null
    // A record's comments live in the comments document it links (RFC 0339),
    // so a comment on it wakes the app as a change to that document. Keyed
    // `<record>\u0000<comments doc>`, reserved before the find like records.
    const commentOff = new Map<string, () => void>()
    const followComments = (recordId: string, doc: unknown) => {
      for (const cid of linkedCommentsIds(doc)) {
        const key = `${recordId}\u0000${cid}`
        if (commentOff.has(key)) continue
        // Followed, not found once: a comments document linked before it has
        // synced here still wakes the app when it arrives (RFC 0339 review).
        let offChange: (() => void) | null = null
        const unfollow = followDocument(repo as never, cid, (h) => {
          if (cancelled || !commentOff.has(key)) return
          const handler = () => notify()
          h.on('change', handler)
          offChange = () => h.off('change', handler)
          notify()
        })
        commentOff.set(key, () => {
          unfollow()
          offChange?.()
        })
      }
    }
    const dropComments = (recordId: string) => {
      for (const [key, off] of commentOff) {
        if (!key.startsWith(`${recordId}\u0000`)) continue
        off()
        commentOff.delete(key)
      }
    }

    void (async () => {
      const ws = await repo.find(projectId as AnyDocumentId)
      await (ws as { whenReady?: () => Promise<void> }).whenReady?.()
      if (cancelled) return

      let instancesKey = ''
      let commentsKey = ''
      const resubscribe = () => {
        const records = projectDocumentsOfType(ws.doc() as ProjectDoc, dataType)
        const nextInstancesKey = appViewInstancesKey(records)
        const instancesChanged = nextInstancesKey !== instancesKey
        instancesKey = nextInstancesKey
        const ids = new Set(records.map((r) => r.id))
        // Drop instances that are gone.
        for (const [id, off] of instanceOff) {
          if (!ids.has(id)) {
            off()
            instanceOff.delete(id)
          }
        }
        // Subscribe to new ones. Reserve the slot before the async find so a
        // second resubscribe can't double-subscribe the same id; the real
        // unsubscribe replaces the placeholder once the handle resolves.
        for (const id of ids) {
          if (instanceOff.has(id)) continue
          instanceOff.set(id, () => {})
          void repo
            .find(id as AnyDocumentId)
            .then((h) => {
              if (cancelled || !instanceOff.has(id)) return
              const doc = () => (h as { doc?: () => unknown }).doc?.()
              // A record's first comment links its comments document, which
              // arrives as a change to the record.
              const handler = () => {
                followComments(id, doc())
                notify()
              }
              ;(h as { on?: (e: string, cb: () => void) => void }).on?.(
                'change',
                handler,
              )
              followComments(id, doc())
              instanceOff.set(id, () => {
                ;(h as { off?: (e: string, cb: () => void) => void }).off?.(
                  'change',
                  handler,
                )
                dropComments(id)
              })
            })
            .catch(() => {
              // Unavailable instance: drop the reservation, keep the app live.
              instanceOff.delete(id)
            })
        }
        return instancesChanged
      }

      const onProjectChange = () => {
        // The project is only a catalogue watcher. Its unrelated changes must
        // not wake an app; its own instance handles below already carry edits,
        // and their comments documents carry comments (RFC 0339). Comments the
        // project still holds from before the move are the exception, so one
        // of those changing wakes it (RFC 0277 review).
        const ids = new Set(
          projectDocumentsOfType(ws.doc() as ProjectDoc, dataType).map(
            (r) => r.id,
          ),
        )
        const nextCommentsKey = appCommentsKey(
          (ws.doc() as { comments?: Record<string, unknown> } | undefined)
            ?.comments,
          ids,
        )
        const commentsChanged = nextCommentsKey !== commentsKey
        commentsKey = nextCommentsKey
        if (resubscribe() || commentsChanged) notify()
      }
      ;(ws as { on?: (e: string, cb: () => void) => void }).on?.(
        'change',
        onProjectChange,
      )
      projectOff = () =>
        (ws as { off?: (e: string, cb: () => void) => void }).off?.(
          'change',
          onProjectChange,
        )
      // Unmounted while awaiting the project doc: tear the watcher back down.
      if (cancelled) {
        projectOff()
        return
      }
      resubscribe()
    })()

    return () => {
      cancelled = true
      control.close()
      onAppControlRef.current?.(null)
      // The channel dies with the frame. Without this every republish and every
      // remount would leave an intermediary repo alive holding granted
      // documents, which is both a leak and a grant that outlives its frame.
      syncRef.current?.stop()
      syncRef.current = null
      if (notifyTimer) clearTimeout(notifyTimer)
      window.removeEventListener('message', onMessage)
      projectOff?.()
      for (const off of instanceOff.values()) off()
      instanceOff.clear()
      for (const off of commentOff.values()) off()
      commentOff.clear()
    }
  }, [repo, projectId, dataType])

  // Navigate the frame IMPERATIVELY, after it is in the document.
  //
  // Rendering `srcDoc` as a prop looks equivalent and is not: the frame that
  // React mounts paints nothing, while assigning `srcdoc` to the same element
  // afterwards paints correctly, with the identical markup and the same
  // sandbox. Both were measured on this component. The failure is silent and
  // total (right document in the DOM, right size, bridge calls all completing,
  // no error anywhere), so it reads as an app that renders nothing rather than
  // as a frame that never navigated.
  //
  // Assigning on every change also means a republish (RFC 0274) re-navigates,
  // which is what makes `mod app push` visible without a page reload.
  useEffect(() => {
    const el = iframeRef.current
    if (el) el.srcdoc = srcDoc
  }, [srcDoc])

  return (
    <iframe
      ref={frameRef}
      title={title}
      data-mod-app-frame=""
      sandbox="allow-scripts"
      // Permissions Policy, not sandbox: fullscreen is delegated to a frame by
      // `allow`, and without it a view's requestFullscreen is rejected. An app
      // that presents (a deck, a canvas shown to a room) can then take over the
      // display instead of the panel it happens to be docked in. Still requires
      // a user gesture inside the frame, so this delegates the ability, not the
      // decision. `microphone` the same way (RFC 0307): an installed app may
      // ask for the mic; the desktop shell still allows audio only.
      allow="fullscreen; microphone"
      // Same as the plain-HTML frame above: not reliably clipped by the
      // parent, so it rounds itself, inheriting so an unframed panel stays
      // square.
      className="h-full w-full flex-1 rounded-[inherit] border-0 bg-white"
    />
  )
}
